import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

import App from '~/App';
import { ErrorBoundary, ErrorFallback } from '~/components/common';

import '~/styles';

import { client } from './api/client.gen';

const root = document.getElementById('root');

const getBaseUrl = () => {
  switch (import.meta.env.VITE_DEPLOY_TARGET) {
    case 'production':
      return 'https://comments.mako.co.il/api/rest';
    case 'development':
      return 'https://dev.comments.mako.co.il/api/rest';
    default:
      return 'http://localhost:8000/rest';
  }
};

client.setConfig({
  baseUrl: getBaseUrl(),
});

if (import.meta.env.VITE_DEPLOY_TARGET === 'production') {
  Sentry.init({
    dsn: 'https://5007557721e5b4a7f81b92d27ae5f6c1@o4509904023453696.ingest.de.sentry.io/4509904029155408',
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: true,
        shouldCreateSpanForRequest: url => url.startsWith(window.location.origin),
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
    environment: import.meta.env.VITE_DEPLOY_TARGET,
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /extension:/i,
      /widgetapp/i, // client specific code
      /webkit-masked-url/i, // https://github.com/getsentry/sentry-javascript/discussions/5875
    ],
    ignoreErrors: [
      /gtag/i, // client specific code
      /googletag/i, // client specific code
      /accessibility/i, // client specific code
      /kWidget/i, // client specific code
      /vcmidOfContent/i, // client specific code
      /Walla/i, // client specific code
      /makoCatDFP/i, // client specific code
      /sentryWrapped/i, // sentry specific code
      /@sentry/i, // sentry specific code
      /webkit-masked-url/i, // https://github.com/getsentry/sentry-javascript/discussions/5875
      /zp_token is not defined/i, // client specific code
    ],
  });
}

if (!root) {
  throw new Error('No root element found');
}

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary fallback={({ message }) => <ErrorFallback message={message} />}>
      <App />
    </ErrorBoundary>
  </StrictMode>
);
